import * as React from 'react'

import { cn } from '../../lib/cn'
import {
  COUNTRIES,
  type Country,
  countryByIso,
  DEFAULT_COUNTRY_ISO,
  flagEmoji,
} from '../../lib/phone-countries'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select'

export interface PhoneInputProps {
  /**
   * Called whenever the country or national number changes, with the
   * composed E.164 value (e.g. "+12025550123"). Empty string when the
   * national part is blank, so callers can treat it as "no input yet".
   */
  onChange?: (e164: string) => void
  /** Initial country (ISO 3166-1 alpha-2). Defaults to US. */
  defaultCountryIso?: string
  /**
   * Optional initial composed E.164 value (e.g. "+919820176439") that
   * the input pre-fills on mount. Parsing logic picks the country with
   * the LONGEST matching dial-code prefix (so "+1..." matches US/CA but
   * "+1..." starting with the right NANP area code stays on whichever
   * country mounts first; the dataset's first entry per dial wins).
   * Takes precedence over `defaultCountryIso` when both are supplied.
   *
   * Critically: this is the state-preservation hook callers use to keep
   * the country selection sticky across form re-mounts (e.g. when a user
   * clicks "Use a different method" on a sign-in OTP picker and returns
   * to SMS). Without this, the input remounts with `defaultCountryIso`
   * (US) and the caller-side stored national digits get assembled with
   * the wrong country code, which gateways like BulkPush correctly
   * reject as "Invalid mobile number".
   */
  defaultE164?: string
  /** Controlled national-number string (digits + spaces tolerated). */
  nationalValue?: string
  disabled?: boolean
  /** Renders the error border treatment. */
  error?: boolean
  /** id applied to the national-number input (for label association). */
  id?: string
  placeholder?: string
  /** aria-label for the number input when there's no visible <label>. */
  'aria-label'?: string
  className?: string
}

/**
 * parseE164Seed splits a composed E.164 value into an initial country +
 * national-digits pair. Returns null when the input doesn't look like
 * E.164 OR no country in COUNTRIES matches the dial prefix; callers fall
 * back to `defaultCountryIso` in that case.
 *
 * Algorithm: strip leading '+', then walk COUNTRIES looking for the
 * LONGEST matching dial code (so "971..." picks UAE over "9..." matches
 * with shorter prefixes; the dial dataset has no 1-digit "9" entry, so
 * the longest-wins rule is what protects North America vs single-digit
 * country codes elsewhere too).
 */
function parseE164Seed(
  raw: string | undefined,
): { iso: string; national: string } | null {
  if (!raw) return null
  if (!raw.startsWith('+')) return null
  const digits = raw.slice(1).replace(/\D/g, '')
  if (digits.length < 2) return null
  let best: { iso: string; dialLen: number } | null = null
  for (const c of COUNTRIES) {
    if (digits.startsWith(c.dial)) {
      if (!best || c.dial.length > best.dialLen) {
        best = { iso: c.iso, dialLen: c.dial.length }
      }
    }
  }
  if (!best) return null
  return { iso: best.iso, national: digits.slice(best.dialLen) }
}

function compose(country: Country, national: string): string {
  const digits = national.replace(/\D/g, '')
  return digits ? `+${country.dial}${digits}` : ''
}

/**
 * PhoneInput — country-code selector + national-number field that emits a
 * single E.164 string. Pure presentation over DS Select + a native input;
 * no validation beyond stripping non-digits (the server validates the wire
 * value). The flag is derived from the ISO code, so the country dataset
 * stays data-only.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      onChange,
      defaultCountryIso = DEFAULT_COUNTRY_ISO,
      defaultE164,
      nationalValue,
      disabled,
      error,
      id,
      placeholder = '555 123 4567',
      className,
      ...rest
    },
    ref,
  ) => {
    // Seed both country + national from defaultE164 when supplied; this
    // is what keeps the selection sticky across LoginForm "Use a
    // different method" remounts. defaultCountryIso is the fallback when
    // no E.164 seed is present (or it's malformed).
    const seed = React.useMemo(() => parseE164Seed(defaultE164), [defaultE164])
    const [iso, setIso] = React.useState(() =>
      seed ? seed.iso : countryByIso(defaultCountryIso).iso,
    )
    const [nationalState, setNationalState] = React.useState(() => (seed ? seed.national : ''))
    const national = nationalValue ?? nationalState
    const country = countryByIso(iso)

    const emit = (nextCountry: Country, nextNational: string) => {
      onChange?.(compose(nextCountry, nextNational))
    }

    const fieldBox = cn(
      'flex h-11 items-center rounded-lg border bg-ia-input-bg transition-colors',
      'focus-within:ring-1 focus-within:ring-ring focus-within:border-ia-border-focus',
      error ? 'border-ia-border-error' : 'border-ia-border',
      disabled && 'opacity-50 cursor-not-allowed',
    )

    return (
      <div className={cn('flex items-stretch gap-2', className)}>
        <Select
          value={iso}
          onValueChange={(next) => {
            setIso(next)
            emit(countryByIso(next), national)
          }}
          disabled={disabled}
        >
          <SelectTrigger
            aria-label="Country calling code"
            className={cn(
              'h-11 w-auto shrink-0 gap-1.5 rounded-lg border-ia-border bg-ia-input-bg px-3 text-ia-text',
              'focus:ring-1 focus:ring-ring focus:ring-offset-0',
            )}
          >
            <SelectValue>
              <span className="flex items-center gap-1.5">
                <span aria-hidden className="text-base leading-none">
                  {flagEmoji(country.iso)}
                </span>
                <span className="text-body-sm font-medium">{country.iso}</span>
                <span className="text-body-sm text-ia-text-tertiary">+{country.dial}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {COUNTRIES.map((c) => (
              <SelectItem key={`${c.iso}-${c.dial}`} value={c.iso} className="pr-3">
                <span className="flex items-center gap-2">
                  <span aria-hidden className="text-base leading-none">
                    {flagEmoji(c.iso)}
                  </span>
                  <span className="text-body-sm text-ia-text">{c.name}</span>
                  <span className="text-body-sm text-ia-text-tertiary">+{c.dial}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className={cn(fieldBox, 'flex-1')}>
          <input
            ref={ref}
            id={id}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            disabled={disabled}
            placeholder={placeholder}
            value={national}
            onChange={(e) => {
              const v = e.target.value
              if (nationalValue === undefined) setNationalState(v)
              emit(country, v)
            }}
            className={cn(
              'h-full w-full rounded-lg bg-transparent px-3.5 text-body-lg text-ia-text',
              'placeholder:text-ia-text-tertiary focus:outline-none disabled:cursor-not-allowed',
            )}
            {...rest}
          />
        </div>
      </div>
    )
  },
)
PhoneInput.displayName = 'PhoneInput'
